import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { useSearchParams } from "react-router-dom";

import { GridStack } from "gridstack";
import type { GridItemHTMLElement, GridStackWidget } from "gridstack";
import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import OffTherapyPatient from "src/components/patientListing/offTherapyPatient";
import OnTherapyPatient from "src/components/patientListing/onTherapyPatient";
import PendingTherapyPatient from "src/components/patientListing/pendingTherapyPatient";
import PatientFilterHCP from "src/components/manufacturer/filters/patientFilterHCP";
import useFetch from "src/hooks/useFetch";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { AuthContext } from "src/context/authContext";
import { initialState, tableFilterReducer } from "./reducer";
import { ToastTypes } from "src/types";
import { APIEndpoints } from "src/types/apiTypes";
import {
  ActionDashboardActionType,
  PatientDashboardApiResponse,
  PatientFiltersState,
  PatientFilterStateActions,
} from "./types";
import { getRole } from "src/helpers";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";
import { addDays, format, subMonths } from "date-fns";

/* ============================================================
 * DEFAULT SECTION ORDER
 * ============================================================
 */

const DEFAULT_SECTION_ORDER = [
  "on_therapy",
  "off_therapy",
  "pending_therapy",
] as const;

type PatientSectionId = (typeof DEFAULT_SECTION_ORDER)[number];

/* ============================================================
 * TYPES
 * ============================================================
 */

interface PatientSectionPosition {
  id: PatientSectionId;
  position: number;

  x: number;
  y: number;
  w: number;
  h: number;
}

interface GridSectionProps {
  id: PatientSectionId;

  title: ReactNode;

  children: ReactNode;

  isCollapsed: boolean;

  onToggleCollapse: () => void;
}

/*
 * Add section_order to the current patient dashboard response.
 *
 * Ideally this property should eventually be moved into your
 * PatientDashboardApiResponse type itself.
 */
type PatientDashboardWithSectionOrderResponse = Omit<
  PatientDashboardApiResponse,
  "data"
> & {
  data: PatientDashboardApiResponse["data"] & {
    section_order?: string[] | null;
  };
};

interface SectionOrderResponse {
  data?: unknown;

  message?: string;
}

/* ============================================================
 * HELPERS
 * ============================================================
 */

const isPatientSectionId = (value: unknown): value is PatientSectionId => {
  return (
    typeof value === "string" &&
    DEFAULT_SECTION_ORDER.includes(value as PatientSectionId)
  );
};

/**
 * Only use the API section order when all expected
 * sections are present exactly once.
 *
 * Otherwise use the default order.
 */
const resolveSectionOrder = (order?: string[] | null): PatientSectionId[] => {
  const validOrder = order?.filter(isPatientSectionId) ?? [];

  const containsEverySection =
    validOrder.length === DEFAULT_SECTION_ORDER.length &&
    new Set(validOrder).size === DEFAULT_SECTION_ORDER.length;

  return containsEverySection ? validOrder : [...DEFAULT_SECTION_ORDER];
};

/**
 * Check whether GridStack still owns a mounted DOM node.
 */
const isGridActive = (grid: GridStack | null): grid is GridStack => {
  return Boolean(grid?.el?.isConnected);
};

/**
 * Read the current visual section order from GridStack.
 */
const getSectionPositions = (grid: GridStack): PatientSectionPosition[] => {
  if (!isGridActive(grid)) {
    return [];
  }

  try {
    return grid
      .getGridItems()
      .map((item) => {
        const id = item.dataset.gridId;

        const node = item.gridstackNode;

        if (!isPatientSectionId(id) || !node) {
          return null;
        }

        return {
          id,

          position: 0,

          x: node.x ?? 0,

          y: node.y ?? 0,

          w: node.w ?? 1,

          h: node.h ?? 1,
        };
      })
      .filter((item): item is PatientSectionPosition => item !== null)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .map((item, index) => ({
        ...item,

        position: index + 1,
      }));
  } catch (error) {
    console.error("Unable to read Patient GridStack positions:", error);

    return [];
  }
};

/**
 * Apply section order to the current GridStack instance.
 *
 * IMPORTANT:
 *
 * We intentionally do NOT pass h.
 *
 * Patient tables have dynamic height depending on:
 *
 * - row count
 * - pagination
 * - filtering
 * - collapse/expand
 *
 * resizeToContent() calculates the correct height.
 */
const applySectionOrder = (
  grid: GridStack,

  order: PatientSectionId[],
): void => {
  if (!isGridActive(grid)) {
    return;
  }

  const items = grid.getGridItems();

  const itemsById = new Map<PatientSectionId, GridItemHTMLElement>();

  items.forEach((item) => {
    const id = item.dataset.gridId;

    if (isPatientSectionId(id)) {
      itemsById.set(id, item);
    }
  });

  let y = 0;

  const layout = order.reduce<GridStackWidget[]>(
    (widgets, id) => {
      const item = itemsById.get(id);

      if (!item) {
        return widgets;
      }

      /*
       * Only used for deciding the starting Y
       * of the following section.
       *
       * We intentionally don't pass h to grid.load().
       */
      const currentHeight = item.gridstackNode?.h ?? 1;

      widgets.push({
        id,

        x: 0,

        y,

        w: 1,

        noResize: true,

        sizeToContent: true,
      });

      y += currentHeight;

      return widgets;
    },

    [],
  );

  if (layout.length === DEFAULT_SECTION_ORDER.length) {
    /*
     * false =
     * don't add/remove React-created elements.
     */
    grid.load(layout, false);
  }
};

/* ============================================================
 * GRID SECTION COMPONENT
 * ============================================================
 */

const GridSection: FC<GridSectionProps> = ({
  id,
  title,
  children,
  isCollapsed,
  onToggleCollapse,
}): JSX.Element => {
  return (
    <div className="grid-stack-item" data-grid-id={id}>
      <div className="grid-stack-item-content">
        <section className="card action-dashboard-grid-card">
          {/* ==================================================
              HEADER
          ================================================== */}

          <div
            className="
              card-header
              custom-bg-purple
              d-flex
              align-items-center
              justify-content-between
              gap-3
            "
          >
            {/* ================================================
                DRAG HANDLE
            ================================================ */}



            {/* ================================================
                TITLE
            ================================================ */}

            <h3 className="text-white mb-0 flex-grow-1">{title}</h3>

            {/* ================================================
                COLLAPSE BUTTON
            ================================================ */}

            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="
                  btn
                  btn-link
                  text-white
                  p-0
                  border-0
                  action-dashboard-collapse-button
                "
                title={isCollapsed ? `Expand ${id}` : `Collapse ${id}`}
                aria-label={
                  isCollapsed
                    ? `Expand ${id} section`
                    : `Collapse ${id} section`
                }
                aria-expanded={!isCollapsed}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();

                  event.stopPropagation();

                  onToggleCollapse();
                }}
              >
                <i
                  className={`fas ${isCollapsed ? "fa-chevron-down" : "fa-chevron-up"
                    }`}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="action-dashboard-drag-handle"
                title={`Move ${id}`}
                aria-label={`Move ${id} section`}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <i className="fas fa-grip-vertical" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* ==================================================
              CONTENT
          ================================================== */}

          {!isCollapsed && <div className="card-body p-0">{children}</div>}
        </section>
      </div>
    </div>
  );
};

/* ============================================================
 * PATIENT LISTING
 * ============================================================
 */

const PatientListing: FC = (): JSX.Element => {
  /* ========================================================
   * CONTEXT
   * ========================================================
   */

  const { userData } = useContext(AuthContext);

  const { showToast } = useContext(ToastContext);

  const { setLoader, hideLoader } = useContext(LoaderContext);

  /* ========================================================
   * URL
   * ========================================================
   */

  const [searchParams, updateSearchParams] = useSearchParams();

  /* ========================================================
   * FILTER STATE
   * ========================================================
   */

  const [queryParams, dispatch] = useReducer(
    tableFilterReducer,

    initialState({
      start_date:
        searchParams.get("start_date") ||
        format(
          subMonths(new Date(), 2),

          "yyyy-MM-dd",
        ),

      end_date:
        searchParams.get("end_date") ||
        format(
          addDays(new Date(), 1),

          "yyyy-MM-dd",
        ),

      case_id: searchParams.get("case_id") || "",

      global_patient_id: searchParams.get("global_patient_id") || "",

      global_doctor_id:
        userData?.ref_type === "GlobalDoctor"
          ? userData.id?.toString()
          : searchParams.get("global_doctor_id") || "",

      practice_id:
        userData?.ref_type === "Practice"
          ? userData?.ref_id.toString()
          : searchParams.get("practice_id") || "",

      global_patient_external_id:
        searchParams.get("global_patient_external_id") || "",

      ref_user:
        (searchParams.get("role") || getRole() || userData?.ref_type) !==
          "Manufacturer"
          ? searchParams.get("id") || userData?.id?.toString() || ""
          : "",

      search_str: searchParams.get("search_str") || "",

      patient_name: searchParams.get("patient_name") || "",

      role: searchParams.get("role") || getRole() || userData?.ref_type || "",

      id: searchParams.get("id") || userData?.id?.toString() || "",

      pending_therapy_page: 1,

      on_therapy_page: 1,

      off_therapy_page: 1,

      pagination: true,
    }),
  );

  /* ========================================================
   * COLLAPSED SECTIONS
   * ========================================================
   */

  const [collapsedSections, setCollapsedSections] = useState<
    Record<PatientSectionId, boolean>
  >({
    on_therapy: false,

    off_therapy: false,

    pending_therapy: false,
  });

  /* ========================================================
   * GRIDSTACK REFS
   * ========================================================
   */

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const gridInstanceRef = useRef<GridStack | null>(null);

  const refreshFrameRef = useRef<number | null>(null);

  const secondRefreshFrameRef = useRef<number | null>(null);

  /*
   * Tracks the visual order currently being displayed.
   */
  const currentOrderRef = useRef<PatientSectionId[]>([
    ...DEFAULT_SECTION_ORDER,
  ]);

  /*
   * Used inside GridStack dragstop to always access
   * the most recent save callback.
   */
  const saveOrderRef = useRef<(order: PatientSectionId[]) => void>(
    () => undefined,
  );

  /*
   * Protects a newly dragged section order from being
   * immediately replaced by an older PATIENT_DASHBOARD
   * response.
   */
  const pendingSectionOrderRef = useRef<PatientSectionId[] | null>(null);

  /* ========================================================
   * INITIAL LOADER
   * ========================================================
   */

  useLayoutEffect(() => {
    setLoader();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ========================================================
   * PRACTICE DROPDOWN
   * ========================================================
   */

  const { data: practiceDropdownApiRes } = useFetch<PracticeDropdownResponse>(
    APIEndpoints.PRACTICE_DROPDOWN,
  );

  /* ========================================================
   * PATIENT DASHBOARD
   * ========================================================
   */

  const {
    data: patientDashboardData,

    mutate: patientDashboardMutate,

    error: patientDashboardError,
  } = useFetch<PatientDashboardWithSectionOrderResponse>(
    APIEndpoints.PATIENT_DASHBOARD,

    queryParams,

    {},

    false,
  );

  /* ========================================================
   * SECTION ORDER UPDATE API
   * ========================================================
   *
   * Add this API endpoint:
   *
   * APIEndpoints.PATIENTDASHBOARD_SECTION_ORDER
   *
   * Request:
   *
   * {
   *   section_order: [
   *     "on_therapy",
   *     "off_therapy",
   *     "pending_therapy"
   *   ]
   * }
   *
   * ========================================================
   */

  const {
    data: sectionOrderUpdateResponse,

    error: sectionOrderUpdateError,

    mutate: updateSectionOrderData,
  } = useFetch<SectionOrderResponse>(
    APIEndpoints.ACTIONDASHBOARD_SECTION_ORDER,

    {},

    {},

    false,
  );

  /* ========================================================
   * API SECTION ORDER
   * ========================================================
   */

  const apiSectionOrder = useMemo(
    () => resolveSectionOrder(patientDashboardData?.data?.section_order),

    [patientDashboardData?.data?.section_order],
  );

  /* ========================================================
   * PATIENT DATA
   * ========================================================
   */

  const on_therapy_patients = useMemo(() => {
    if (patientDashboardData?.data?.on_treatment) {
      return patientDashboardData.data.on_treatment;
    }

    return undefined;
  }, [patientDashboardData?.data?.on_treatment]);

  const off_therapy_patients = useMemo(() => {
    if (patientDashboardData?.data?.off_treatment) {
      return patientDashboardData.data.off_treatment;
    }

    return undefined;
  }, [patientDashboardData?.data?.off_treatment]);

  const pending_treatment = useMemo(() => {
    if (patientDashboardData?.data?.pending_treatment) {
      return patientDashboardData.data.pending_treatment;
    }

    return undefined;
  }, [patientDashboardData?.data?.pending_treatment]);

  /* ========================================================
   * PATIENT API RESULT
   * ========================================================
   */

  useEffect(() => {
    if (patientDashboardData || patientDashboardError) {
      hideLoader();

      if (patientDashboardData) {
        showToast(
          ["Patient data fetched successfully"],

          ToastTypes.SUCCESS,
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDashboardData, patientDashboardError]);

  /* ========================================================
   * INITIAL PATIENT API BASED ON USER TYPE
   * ========================================================
   */

  useEffect(() => {
    if (["Contact", "Practice"].includes(userData?.ref_type || "")) {
      if (practiceDropdownApiRes) {
        const practiceId = practiceDropdownApiRes.data?.[0]?.id.toString();

        const { start_date, end_date, ...cleanedQueries } = queryParams;

        patientDashboardMutate(
          {},
          {},
          {
            ...cleanedQueries,

            practice_id: practiceId,
          },
        );
      }
    } else if (["Organization"].includes(userData?.ref_type || "")) {
      const { start_date, end_date, ...cleanedQueries } = queryParams;

      patientDashboardMutate({}, {}, cleanedQueries);
    } else if (["GlobalDoctor"].includes(userData?.ref_type || "")) {
      const { start_date, end_date, ...cleanedQueries } = queryParams;

      patientDashboardMutate(
        {},
        {},
        {
          ...cleanedQueries,

          global_doctor_id: userData?.ref_id?.toString() ?? "",
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceDropdownApiRes]);

  /* ========================================================
   * SAVE SECTION ORDER
   * ========================================================
   */

  const saveSectionOrder = useCallback(
    (order: PatientSectionId[]): void => {
      /*
       * Use this order immediately in the UI.
       */
      currentOrderRef.current = order;

      /*
       * Mark it as currently being persisted.
       */
      pendingSectionOrderRef.current = order;

      /*
       * Same API save format as ActionDashboard.
       */
      updateSectionOrderData({
        section_order: order,
        page: "patient",
      });
    },

    [updateSectionOrderData],
  );

  useEffect(() => {
    saveOrderRef.current = saveSectionOrder;
  }, [saveSectionOrder]);

  /* ========================================================
   * GRID REFRESH
   * ========================================================
   *
   * React render
   *       ↓
   * Apply current order
   *       ↓
   * Browser measures tables
   *       ↓
   * resizeToContent()
   *       ↓
   * compact()
   *
   * ========================================================
   */

  const scheduleGridRefresh = useCallback(
    (grid: GridStack): void => {
      /*
       * Cancel previous refresh.
       */
      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);

        refreshFrameRef.current = null;
      }

      if (secondRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(secondRefreshFrameRef.current);

        secondRefreshFrameRef.current = null;
      }

      /*
       * First animation frame:
       *
       * Give React time to update the DOM.
       */
      refreshFrameRef.current = window.requestAnimationFrame(() => {
        refreshFrameRef.current = null;

        /*
         * Second animation frame:
         *
         * Give table/browser layout time to
         * calculate actual height.
         */
        secondRefreshFrameRef.current = window.requestAnimationFrame(() => {
          secondRefreshFrameRef.current = null;

          if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
            return;
          }

          /*
           * Measure after React restores the body. The first measurement
           * gives applySectionOrder() the current expanded height.
           */
          grid.getGridItems().forEach((item) => {
            grid.resizeToContent(item);
          });

          applySectionOrder(grid, currentOrderRef.current);

          /*
           * load() can update widget geometry, so measure once more before
           * compacting the dashboard.
           */
          grid.getGridItems().forEach((item) => {
            grid.resizeToContent(item);
          });

          /*
           * Remove blank space after collapse,
           * pagination, filtering, etc.
           */
          grid.compact("list");
        });
      });
    },

    [],
  );

  /* ========================================================
   * COLLAPSE / EXPAND
   * ========================================================
   */

  const toggleSectionCollapse = useCallback(
    (id: PatientSectionId): void => {
      setCollapsedSections((previous) => ({
        ...previous,

        [id]: !previous[id],
      }));
    },

    [],
  );

  /*
   * Card body is added/removed by React.
   * Recalculate GridStack height afterwards.
   */
  useEffect(() => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    scheduleGridRefresh(grid);
  }, [collapsedSections, scheduleGridRefresh]);

  /* ========================================================
   * TABLE DATA HEIGHT REFRESH
   * ========================================================
   */

  useEffect(() => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    /*
     * Patient table heights can change after:
     *
     * - filter
     * - pagination
     * - patient action
     * - API refresh
     */
    scheduleGridRefresh(grid);
  }, [
    on_therapy_patients,
    off_therapy_patients,
    pending_treatment,
    scheduleGridRefresh,
  ]);

  /* ========================================================
   * INITIALIZE GRIDSTACK
   * ========================================================
   */

  useEffect(() => {
    const container = gridContainerRef.current;

    if (!container || gridInstanceRef.current) {
      return;
    }

    const initializedGrid = GridStack.init(
      {
        /*
         * Patient tables are full width.
         */
        column: 1,

        /*
         * Gives resizeToContent finer height control.
         */
        cellHeight: 10,

        margin: 12,

        disableResize: true,

        float: false,

        animate: true,

        sizeToContent: true,

        /*
         * User can only drag from this button.
         */
        handle: ".action-dashboard-drag-handle",

        placeholderClass: "grid-stack-placeholder",
      },

      container,
    );

    if (!initializedGrid) {
      console.error("Patient GridStack initialization failed");

      return;
    }

    const grid: GridStack = initializedGrid;

    gridInstanceRef.current = grid;

    /* ======================================================
     * CONFIGURE REACT-CREATED WIDGETS
     * ======================================================
     */

    grid.getGridItems().forEach((item) => {
      const id = item.dataset.gridId;

      if (!isPatientSectionId(id)) {
        return;
      }

      grid.update(
        item,

        {
          id,

          x: 0,

          w: 1,

          noResize: true,

          sizeToContent: true,
        },
      );
    });

    /* ======================================================
     * INITIAL ORDER
     * ======================================================
     */

    applySectionOrder(
      grid,

      currentOrderRef.current,
    );

    scheduleGridRefresh(grid);

    /* ======================================================
     * DRAG STOP
     * ======================================================
     */

    const handleDragStop = (
      _event: Event,

      draggedElement: GridItemHTMLElement,
    ): void => {
      if (gridInstanceRef.current !== grid || !isGridActive(grid)) {
        return;
      }

      const positions = getSectionPositions(grid);

      /*
       * Don't save a broken/incomplete layout.
       */
      if (positions.length !== DEFAULT_SECTION_ORDER.length) {
        return;
      }

      const order = positions.map(({ id }) => id);

      /*
       * Immediately use the new order.
       */
      currentOrderRef.current = order;

      console.log("Dragged patient section:", draggedElement.dataset.gridId);

      console.log("Updated patient section order:", order);

      console.table(positions);

      /*
       * Save to backend.
       */
      saveOrderRef.current(order);

      /*
       * Normalize vertical positions and height.
       */
      scheduleGridRefresh(grid);
    };

    grid.on(
      "dragstop",

      handleDragStop,
    );

    /* ======================================================
     * CLEANUP
     * ======================================================
     */

    return () => {
      if (refreshFrameRef.current !== null) {
        window.cancelAnimationFrame(refreshFrameRef.current);

        refreshFrameRef.current = null;
      }

      if (secondRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(secondRefreshFrameRef.current);

        secondRefreshFrameRef.current = null;
      }

      if (gridInstanceRef.current === grid) {
        gridInstanceRef.current = null;
      }

      if (isGridActive(grid)) {
        grid.off("dragstop");

        /*
         * React owns the elements.
         *
         * false prevents GridStack from deleting them.
         */
        grid.destroy(false);
      }
    };
  }, [scheduleGridRefresh]);

  /* ========================================================
   * APPLY SECTION ORDER FROM PATIENT API
   * ========================================================
   */

  useEffect(() => {
    if (!patientDashboardData) {
      return;
    }

    const pendingOrder = pendingSectionOrderRef.current;

    const serverOrderKey = apiSectionOrder.join("|");

    const pendingOrderKey = pendingOrder?.join("|");

    /*
     * Example:
     *
     * User just dragged:
     *
     * pending -> on -> off
     *
     * but another PATIENT_DASHBOARD request returns an
     * older:
     *
     * on -> off -> pending
     *
     * Don't immediately overwrite the user's drag.
     */
    if (pendingOrder && serverOrderKey !== pendingOrderKey) {
      return;
    }

    /*
     * Store server-approved order.
     */
    currentOrderRef.current = apiSectionOrder;

    if (serverOrderKey === pendingOrderKey) {
      pendingSectionOrderRef.current = null;
    }

    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    applySectionOrder(
      grid,

      apiSectionOrder,
    );

    scheduleGridRefresh(grid);
  }, [patientDashboardData, apiSectionOrder, scheduleGridRefresh]);

  /* ========================================================
   * SECTION ORDER SAVE RESPONSE
   * ========================================================
   */

  useEffect(() => {
    if (sectionOrderUpdateError) {
      console.error(
        "Unable to update Patient Dashboard section order:",
        sectionOrderUpdateError,
      );

      pendingSectionOrderRef.current = null;

      return;
    }

    if (!sectionOrderUpdateResponse) {
      return;
    }

    /*
     * Successful save.
     */
    pendingSectionOrderRef.current = null;
  }, [sectionOrderUpdateError, sectionOrderUpdateResponse]);

  /* ========================================================
   * PAGINATION
   * ========================================================
   */

  const onChangingPage = (action: ActionDashboardActionType): void => {
    const updatedData = tableFilterReducer(
      queryParams,

      action,
    );

    dispatch(action);

    setLoader();

    const { start_date, end_date, ...cleanedQueries } = updatedData;

    patientDashboardMutate({}, {}, cleanedQueries);
  };

  /* ========================================================
   * FILTERS
   * ========================================================
   */

  const updateQueryParamsAndCallAPI = (
    updatedQueries: PatientFiltersState,

    isReset: boolean = false,
  ): void => {
    setLoader();

    /* ======================================================
     * RESET FILTER
     * ======================================================
     */

    if (isReset) {
      const defaultQueryParams = tableFilterReducer(
        updatedQueries,

        {
          type: PatientFilterStateActions.RESET_FILTERS,

          payload: updatedQueries,
        },
      );

      const { start_date, end_date, ...cleanedQueries } = defaultQueryParams;

      patientDashboardMutate({}, {}, cleanedQueries);

      dispatch({
        type: PatientFilterStateActions.RESET_FILTERS,

        payload: defaultQueryParams,
      });

      return;
    }

    /* ======================================================
     * APPLY FILTERS
     * ======================================================
     */

    const updatedParams = {
      ...queryParams,
    };

    updatedParams.case_id = updatedQueries.case_id;

    updatedParams.global_doctor_id = updatedQueries.global_doctor_id;

    updatedParams.global_patient_id = updatedQueries.global_patient_id;

    updatedParams.patient_name = updatedQueries.patient_name;

    updatedParams.practice_id = updatedQueries.practice_id;

    updatedParams.start_date = updatedQueries.start_date;

    updatedParams.end_date = updatedQueries.end_date;

    updatedParams.pending_therapy_page = 1;

    updatedParams.on_therapy_page = 1;

    updatedParams.off_therapy_page = 1;


    const params = new URLSearchParams(
      updatedParams as unknown as Record<string, string>,
    );

    updateSearchParams(
      params.toString(),

      {
        replace: false,
      },
    );

    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,

      payload: updatedParams,
    });

    const { start_date, end_date, ...cleanedQueries } = updatedParams;

    patientDashboardMutate({}, {}, cleanedQueries);
  };

  /* ========================================================
   * SYNC QUERY PARAMS TO URL
   * ========================================================
   */

  useEffect(() => {
    if (queryParams) {
      const params = new URLSearchParams(
        queryParams as unknown as Record<string, string>,
      );

      updateSearchParams(
        params.toString(),

        {
          replace: false,
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  /* ========================================================
   * RESET SECTION ORDER
   * ========================================================
   */

  const resetDashboardLayout = (): void => {
    const grid = gridInstanceRef.current;

    if (!isGridActive(grid)) {
      return;
    }

    const defaultOrder: PatientSectionId[] = [...DEFAULT_SECTION_ORDER];

    /*
     * Set default order locally.
     */
    currentOrderRef.current = defaultOrder;

    /*
     * Apply immediately.
     */
    applySectionOrder(
      grid,

      defaultOrder,
    );

    /*
     * Measure table/card heights.
     */
    scheduleGridRefresh(grid);

    /*
     * Persist default order.
     */
    saveOrderRef.current(defaultOrder);
  };

  /* ========================================================
   * REFRESH CURRENT PATIENT DATA
   * ========================================================
   */

  const refreshPatientListing = (): void => {
    const { start_date, end_date, ...cleanedQueries } = queryParams;

    patientDashboardMutate({}, {}, cleanedQueries);
  };

  /* ========================================================
   * RENDER GRID SECTION
   * ========================================================
   */

  const renderSection = (id: PatientSectionId): JSX.Element => {
    switch (id) {
      /* ====================================================
       * ON THERAPY
       * ====================================================
       */

      case "on_therapy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Patient Currently on Treatment"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <OnTherapyPatient
              dataList={
                on_therapy_patients ? on_therapy_patients.on_therapy : []
              }
              totalPages={
                on_therapy_patients ? on_therapy_patients.page_count : 0
              }
              currentPage={queryParams.on_therapy_page}
              changePageNumber={onChangingPage}
            />
          </GridSection>
        );

      /* ====================================================
       * OFF THERAPY
       * ====================================================
       */

      case "off_therapy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Patient Not Receiving Treatment"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <OffTherapyPatient
              dataList={
                off_therapy_patients ? off_therapy_patients.off_therapy : []
              }
              totalPages={
                off_therapy_patients ? off_therapy_patients.page_count : 0
              }
              currentPage={queryParams.off_therapy_page}
              changePageNumber={onChangingPage}
            />
          </GridSection>
        );

      /* ====================================================
       * PENDING THERAPY
       * ====================================================
       */

      case "pending_therapy":
        return (
          <GridSection
            key={id}
            id={id}
            title="Patient Pending Treatment"
            isCollapsed={collapsedSections[id]}
            onToggleCollapse={() => toggleSectionCollapse(id)}
          >
            <PendingTherapyPatient
              dataList={
                pending_treatment ? pending_treatment.pending_therapy : []
              }
              totalPages={pending_treatment ? pending_treatment.page_count : 0}
              currentPage={queryParams.pending_therapy_page}
              changePageNumber={onChangingPage}
              refreshListing={refreshPatientListing}
            />
          </GridSection>
        );
    }
  };

  /* ========================================================
   * RENDER
   * ========================================================
   */

  return (
    <div
      className="
        container-fluid
        overflow-y-auto
        overflow-x-auto
        d-flex
        flex-column
        flex-fill
      "
    >
      {/* ====================================================
          HEADER
      ==================================================== */}

      <AuthSubHeader />

      {/* ====================================================
          PATIENT FILTER
      ==================================================== */}

      <div className="card">
        <PatientFilterHCP
          doctorDropdown={[]}
          practiceDropdown={practiceDropdownApiRes?.data ?? []}
          defaultQueryParams={queryParams}
          updateQueryParams={updateQueryParamsAndCallAPI}
        />
      </div>

      {/* ====================================================
          RESET SECTION ORDER
      ==================================================== */}

      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="
            btn
            btn-outline-secondary
            btn-sm
            action-dashboard-reset-button
            me-3
          "
          onClick={resetDashboardLayout}
        >
          <i className="fas fa-undo-alt me-2" aria-hidden="true" />
          Reset Section Order
        </button>
      </div>

      {/* ====================================================
          GRIDSTACK
      ==================================================== */}

      <div className="d-flex flex-column">
        <div
          ref={gridContainerRef}
          className="
            grid-stack
            action-dashboard-grid
            mt-3
          "
        >
          {DEFAULT_SECTION_ORDER.map(renderSection)}
        </div>
      </div>

      {/* ====================================================
          FOOTER
      ==================================================== */}

      <FooterText />
    </div>
  );
};

export default PatientListing;
